/**
 * ReportePasajerosPage — Reporte "Pasajeros por tour" con filtros, vista previa
 * y botón de descarga PDF (idéntico al PDF que se generó desde el chat).
 *
 * Backend:
 *   GET /turismo/reportes/pasajeros-por-tour       → JSON con la data
 *   GET /turismo/reportes/pasajeros-por-tour.pdf   → PDF descargable
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, TextField, MenuItem, Button, Stack, CircularProgress,
  Alert, LinearProgress, IconButton, Tooltip, Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useNavigate } from 'react-router-dom';
import { API_BASE, getEmpresaId, getAuthHeaders } from '../booking/shared/format';

const fmt = (n) => (n || 0).toLocaleString('es-CO');
const pct = (a, b) => (b > 0 ? (a / b * 100).toFixed(1) : '0.0');

// Fecha por defecto: mes actual completo (día 1 → hoy).
const todayISO = () => new Date().toISOString().slice(0, 10);
const firstDayOfMonthISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
// Rango sugerido para "desde inicio de operación": jul-06-2026 (primera reserva)
const OPERACION_INICIO = '2026-07-06';

export default function ReportePasajerosPage() {
  const navigate = useNavigate();
  const empresaId = getEmpresaId();
  const [fechaDesde, setFechaDesde] = useState(OPERACION_INICIO);
  const [fechaHasta, setFechaHasta] = useState(todayISO());
  const [agenciaId, setAgenciaId] = useState('');
  const [paqueteId, setPaqueteId] = useState('');

  const [agencias, setAgencias] = useState([]);
  const [paquetes, setPaquetes] = useState([]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Cargar catálogos de filtros
  useEffect(() => {
    if (!empresaId) return;
    fetch(`${API_BASE}/turismo/agencias?empresa_id=${empresaId}&solo_activas=true`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(list => setAgencias(Array.isArray(list) ? list : []))
      .catch(() => setAgencias([]));
    fetch(`${API_BASE}/turismo/paquetes?empresa_id=${empresaId}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(list => setPaquetes(Array.isArray(list) ? list : []))
      .catch(() => setPaquetes([]));
  }, [empresaId]);

  const cargarReporte = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({
        empresa_id: empresaId,
        fecha_desde: fechaDesde,
        fecha_hasta: fechaHasta,
      });
      if (agenciaId) qs.append('agencia_id', agenciaId);
      if (paqueteId) qs.append('paquete_id', paqueteId);

      const r = await fetch(`${API_BASE}/turismo/reportes/pasajeros-por-tour?${qs}`, {
        headers: getAuthHeaders(),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${r.status}`);
      }
      const json = await r.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [empresaId, fechaDesde, fechaHasta, agenciaId, paqueteId]);

  useEffect(() => { cargarReporte(); }, []);  // eslint-disable-line

  const descargarPDF = () => {
    const qs = new URLSearchParams({
      empresa_id: empresaId,
      fecha_desde: fechaDesde,
      fecha_hasta: fechaHasta,
    });
    if (agenciaId) qs.append('agencia_id', agenciaId);
    if (paqueteId) qs.append('paquete_id', paqueteId);
    // Abre en pestaña nueva — respeta la sesión del usuario y triggerea
    // la descarga en el navegador con el filename del server.
    window.open(`${API_BASE}/turismo/reportes/pasajeros-por-tour.pdf?${qs}`, '_blank');
  };

  // Colores rotatorios para los tours (mismo patrón que el PDF)
  const TOUR_COLORS = ['#0891B2', '#F59E0B', '#10B981', '#8B5CF6', '#3B82F6', '#B45309'];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{
          width: 44, height: 44, borderRadius: 2,
          background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', boxShadow: '0 6px 16px rgba(8,145,178,0.4)', flexShrink: 0,
        }}>
          <AssessmentIcon sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
            Pasajeros por tour
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Movimiento operativo por producto turístico · filtra por período, agencia o tour específico.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PictureAsPdfIcon />}
          onClick={descargarPDF}
          disabled={loading || !data}
          sx={{
            textTransform: 'none',
            background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
            boxShadow: '0 4px 12px rgba(8,145,178,0.4)',
            fontWeight: 700,
          }}
        >
          Descargar PDF
        </Button>
      </Box>

      {/* Filtros */}
      <Box sx={{
        p: 2, mb: 2, bgcolor: '#fff', borderRadius: 2,
        border: '1px solid #e2e8f0',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr) auto' },
        gap: 1.5, alignItems: 'end',
      }}>
        <TextField
          size="small" type="date" label="Desde"
          InputLabelProps={{ shrink: true }}
          value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
        />
        <TextField
          size="small" type="date" label="Hasta"
          InputLabelProps={{ shrink: true }}
          value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
        />
        <TextField
          size="small" select label="Agencia"
          value={agenciaId} onChange={e => setAgenciaId(e.target.value)}
        >
          <MenuItem value="">Todas las agencias</MenuItem>
          {agencias.map(a => (
            <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" select label="Tour"
          value={paqueteId} onChange={e => setPaqueteId(e.target.value)}
        >
          <MenuItem value="">Todos los tours</MenuItem>
          {paquetes.map(p => (
            <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined" size="small" startIcon={<RefreshIcon />}
          onClick={cargarReporte} disabled={loading}
          sx={{ textTransform: 'none', minWidth: 130 }}
        >
          Aplicar filtros
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2, fontSize: 13 }}>{error}</Alert>}

      {!loading && data && (
        <>
          {/* KPI bar */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1.5, mb: 3,
          }}>
            {/* Decía "efectivamente embarcados" y no lo es: el backend parte
                las reservas según si la fecha de su salida ya pasó, sin mirar
                el check-in. Mide programación cumplida. El embarque real
                —con cortesías— está en "Embarques por rango". */}
            <KpiTile label="Pasajeros salidos" value={fmt(data.total_pax_salidos)}
                     sub="de salidas ya realizadas" color="#0e7490" />
            <KpiTile label="Pasajeros programados" value={fmt(data.total_pax_programados)}
                     sub="reservados a futuro" color="#0891b2" />
            <KpiTile label="Total reservas" value={fmt(data.total_reservas)}
                     sub="activas (no canceladas)" color="#b45309" />
            <KpiTile label="Tours en operación" value={`${data.tours_activos} / ${data.tours_totales}`}
                     sub={`${data.tours_totales - data.tours_activos} sin actividad`}
                     color="#134e4a" />
          </Box>

          {/* Este reporte mide lo vendido y programado. El que mide quién
              subió al bote es otro, y hay que decirlo acá o alguien va a
              usar estas cifras para responderle a la autoridad marítima. */}
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', mb: 3,
                     p: 1.25, bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
                     borderRadius: 1.5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12, color: '#475569' }}>
              Estas cifras salen de la <strong>fecha programada</strong> de cada
              salida, no del check-in. Para saber quién embarcó de verdad —con
              hora, bote y cortesías incluidas—
            </Typography>
            <Button size="small" variant="text"
              onClick={() => navigate('/reportes/embarques')}
              sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, p: 0.25,
                    minWidth: 0 }}>
              abrí Embarques por rango
            </Button>
          </Box>

          {/* Ranking por tour */}
          <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', mb: 0.5 }}>
              Ranking · pasajeros por tour
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>
              {data.periodo_label}
            </Typography>
            {data.tours_activos_list.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: 12 }}>
                Sin actividad en el período/filtros seleccionados.
              </Alert>
            ) : (
              <Stack spacing={1}>
                {data.tours_activos_list.map((tour, i) => {
                  const color = TOUR_COLORS[i % TOUR_COLORS.length];
                  const maxPax = data.tours_activos_list[0].pax_total;
                  const barPct = maxPax ? (tour.pax_total / maxPax * 100) : 0;
                  const promedio = tour.reservas_total ? (tour.pax_total / tour.reservas_total).toFixed(1) : '0';
                  return (
                    <Box key={tour.paquete_id} sx={{
                      p: 1.5, borderRadius: 1.5, border: '1px solid #e2e8f0',
                      borderLeft: `4px solid ${color}`, bgcolor: '#fff',
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color, letterSpacing: 1 }}>
                              #{i + 1}
                            </Typography>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }} noWrap>
                              {tour.nombre}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>
                            {tour.reservas_total} reservas · promedio {promedio} pax/reserva
                            &nbsp;·&nbsp;{tour.pax_salidos} salidos + {tour.pax_programados} programados
                          </Typography>
                          <Box sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: '#e2e8f0', position: 'relative', overflow: 'hidden' }}>
                            <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${barPct}%`, bgcolor: color }} />
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontSize: 22, fontWeight: 800, color }}>
                            {fmt(tour.pax_total)}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: '#64748b' }}>
                            {pct(tour.pax_total, data.total_pax)}% del total
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
            {data.tours_inactivos_names.length > 0 && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                  Tours sin actividad en el período:
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#78350f' }}>
                  {data.tours_inactivos_names.join('  ·  ')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Composición demográfica */}
          <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0', mb: 2 }}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
              Composición demográfica global
            </Typography>
            <DemoStack
              adultos={data.total_adultos}
              ninos={data.total_ninos}
              bebes={data.total_bebes}
            />
          </Box>

          {/* Top 10 días + Canales */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 2, mb: 2,
          }}>
            <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
                Top 10 días con más pasajeros
              </Typography>
              <TopDiasList dias={data.top_dias} />
            </Box>
            <Box sx={{ p: 2.5, bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
                Canales de venta
              </Typography>
              <CanalesList canales={data.canales} totalPax={data.total_pax} />
            </Box>
          </Box>

          {/* Insight */}
          {data.insight && (
            <Box sx={{
              p: 2, borderRadius: 2, bgcolor: '#0b1220', color: '#fff',
              borderLeft: '4px solid #f59e0b',
            }}>
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f59e0b', letterSpacing: 1.5, mb: 0.5 }}>
                INSIGHT CLAVE
              </Typography>
              <Typography sx={{ fontSize: 13, color: '#e2e8f0' }}>
                {data.insight}
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// ─── Sub-componentes ─────────────────────────────────────────────
function KpiTile({ label, value, sub, color }) {
  return (
    <Box sx={{
      p: 1.75, borderRadius: 2, bgcolor: color, color: '#fff',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minHeight: 88,
    }}>
      <Typography sx={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.4, color: '#f59e0b' }}>
        {label.toUpperCase()}
      </Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, mt: 0.5 }}>
        {value}
      </Typography>
      <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', mt: 0.25 }}>
        {sub}
      </Typography>
    </Box>
  );
}

function DemoStack({ adultos, ninos, bebes }) {
  const total = adultos + ninos + bebes;
  if (total === 0) return <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Sin datos.</Typography>;
  const segs = [
    { label: 'Adultos', v: adultos, color: '#3b82f6' },
    { label: 'Niños',   v: ninos,   color: '#8b5cf6' },
    { label: 'Bebés',   v: bebes,   color: '#f59e0b' },
  ];
  return (
    <>
      <Box sx={{ display: 'flex', height: 16, borderRadius: 1, overflow: 'hidden', mb: 1.5 }}>
        {segs.filter(s => s.v > 0).map(s => (
          <Box key={s.label} sx={{ width: `${s.v / total * 100}%`, bgcolor: s.color }} />
        ))}
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap">
        {segs.map(s => (
          <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>
              {s.label}: {fmt(s.v)} ({pct(s.v, total)}%)
            </Typography>
          </Box>
        ))}
      </Stack>
    </>
  );
}

function TopDiasList({ dias }) {
  if (!dias || dias.length === 0) return <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Sin datos.</Typography>;
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  const maxPax = Math.max(...dias.map(d => d.pax));
  return (
    <Stack spacing={0.5}>
      {dias.map((d, i) => {
        const [y, m, day] = d.fecha.split('-');
        const fechaFmt = `${parseInt(day)} ${meses[parseInt(m) - 1]} ${y}`;
        const pctBar = maxPax ? (d.pax / maxPax * 100) : 0;
        return (
          <Box key={d.fecha} sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            p: 0.75, borderRadius: 0.75,
            bgcolor: i % 2 === 0 ? '#f8fafc' : 'transparent',
          }}>
            <Typography sx={{ fontSize: 11, color: '#94a3b8', width: 20, fontWeight: 700 }}>
              {String(i + 1).padStart(2, '0')}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a', minWidth: 100 }}>
              {fechaFmt}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b', minWidth: 80 }}>
              {d.reservas} reservas
            </Typography>
            <Box sx={{ flex: 1, height: 8, bgcolor: '#e2e8f0', borderRadius: 4, position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${pctBar}%`, bgcolor: '#0891b2' }} />
            </Box>
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0e7490', minWidth: 40, textAlign: 'right' }}>
              {fmt(d.pax)}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
}

function CanalesList({ canales, totalPax }) {
  if (!canales || canales.length === 0) return <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>Sin datos.</Typography>;
  const colors = ['#0e7490', '#f59e0b'];
  return (
    <Stack spacing={2}>
      {canales.map((c, i) => (
        <Box key={c.canal}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colors[i] }} />
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{c.canal}</Typography>
          </Box>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: colors[i], lineHeight: 1 }}>
            {fmt(c.pax)}
            <Box component="span" sx={{ fontSize: 11, color: '#64748b', ml: 0.75, fontWeight: 500 }}>
              pasajeros
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.5 }}>
            {c.reservas} reservas · {pct(c.pax, totalPax)}% del total
          </Typography>
        </Box>
      ))}
    </Stack>
  );
}
